import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, X } from "lucide-react";
import { API, formatRelative } from "./shared";

// ============================================================
// INBOX BELL — in-app notification feed
// ============================================================
export function InboxBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const loadUnread = async () => {
    try {
      const r = await axios.get(`${API}/notifications/unread-count`);
      setUnread(r.data?.unread || 0);
    } catch { /* unauth / network — stay silent */ }
  };
  const loadFull = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/notifications`, { params: { limit: 20 } });
      setItems(r.data?.items || []);
      setUnread(r.data?.unread || 0);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => {
    loadUnread();
    const t = setInterval(loadUnread, 30000); // poll every 30s
    return () => clearInterval(t);
  }, []);
  useEffect(() => { if (open) loadFull(); }, [open]);
  const markRead = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/read`);
      setItems(xs => xs.map(x => x.id === id ? { ...x, read_at: new Date().toISOString() } : x));
      setUnread(n => Math.max(0, n - 1));
    } catch { /* silent */ }
  };
  const markAll = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`);
      setItems(xs => xs.map(x => ({ ...x, read_at: x.read_at || new Date().toISOString() })));
      setUnread(0);
    } catch { /* silent */ }
  };
  const toneStyles = {
    info:    { chip:"#E2E8F0", chipFg:"#334155" },
    success: { chip:"#DCFCE7", chipFg:"#065F46" },
    warn:    { chip:"#FEF3C7", chipFg:"#92400E" },
    action:  { chip:"#DBEAFE", chipFg:"#1E40AF" },
  };
  return (
    <>
      <button
        data-testid="notification-button"
        className="icon-button"
        onClick={() => setOpen(true)}
        style={{position:"relative"}}
        aria-label={`Notifications (${unread} unread)`}
      >
        <Bell size={18}/>
        {unread > 0 && (
          <span
            data-testid="notification-badge"
            style={{
              position:"absolute", top:-2, right:-2,
              background:"#DC2626", color:"#fff",
              borderRadius:999, minWidth:18, height:18,
              fontSize:10, fontWeight:800,
              display:"flex", alignItems:"center", justifyContent:"center",
              padding:"0 5px", lineHeight:1,
            }}
          >{unread > 99 ? "99+" : unread}</span>
        )}
      </button>
      {open && (
        <div
          data-testid="inbox-drawer-overlay"
          onClick={() => setOpen(false)}
          style={{
            position:"fixed", inset:0, background:"rgba(15,23,42,0.35)",
            zIndex:95,
          }}
        />
      )}
      {open && (
        <aside
          data-testid="inbox-drawer"
          style={{
            position:"fixed", top:0, right:0, bottom:0, width:380, maxWidth:"92vw",
            background:"#fff", borderLeft:"1px solid #E2E8F0", zIndex:96,
            display:"flex", flexDirection:"column",
            boxShadow:"-8px 0 24px rgba(15,23,42,0.08)",
          }}
          onClick={e => e.stopPropagation()}
        >
          <header style={{
            display:"flex", justifyContent:"space-between", alignItems:"center",
            padding:"16px 18px", borderBottom:"1px solid #E2E8F0",
          }}>
            <div>
              <b style={{fontSize:15}}>Inbox</b>
              <div style={{fontSize:11, color:"#64748B", marginTop:2}}>
                {unread > 0 ? `${unread} unread` : "All caught up"}
              </div>
            </div>
            <div style={{display:"flex", gap:6, alignItems:"center"}}>
              {unread > 0 && (
                <button
                  data-testid="inbox-mark-all"
                  onClick={markAll}
                  style={{background:"transparent", border:"none", fontSize:11, color:"#2563EB", cursor:"pointer", padding:"4px 8px"}}
                >Mark all read</button>
              )}
              <button
                data-testid="inbox-close"
                onClick={() => setOpen(false)}
                style={{background:"transparent", border:"none", cursor:"pointer", padding:4, display:"flex"}}
                aria-label="Close inbox"
              ><X size={18}/></button>
            </div>
          </header>
          <div style={{flex:1, overflowY:"auto"}}>
            {loading && (
              <div style={{padding:"24px 18px", color:"#64748B", fontSize:12}}>Loading…</div>
            )}
            {!loading && items.length === 0 && (
              <div style={{padding:"40px 20px", textAlign:"center", color:"#64748B"}}>
                <Bell size={28} style={{opacity:0.4, margin:"0 auto 10px"}}/>
                <div style={{fontSize:13, fontWeight:600, color:"#334155"}}>No notifications yet</div>
                <div style={{fontSize:11, marginTop:4}}>You'll see live auction, award, and delivery alerts here.</div>
              </div>
            )}
            {!loading && items.map(it => {
              const isUnread = !it.read_at;
              const tone = toneStyles[it.severity] || toneStyles.info;
              return (
                <div
                  key={it.id}
                  data-testid={`inbox-item-${it.id}`}
                  data-unread={isUnread ? "1" : "0"}
                  onClick={() => isUnread && markRead(it.id)}
                  style={{
                    display:"flex", gap:10, padding:"12px 18px",
                    borderBottom:"1px solid #F1F5F9",
                    background: isUnread ? "#F8FAFC" : "transparent",
                    cursor: isUnread ? "pointer" : "default",
                  }}
                >
                  <span style={{
                    width:8, height:8, marginTop:7, borderRadius:999,
                    background: isUnread ? "#2563EB" : "transparent",
                    flexShrink:0,
                  }}/>
                  <div style={{flex:1, minWidth:0}}>
                    <div style={{display:"flex", justifyContent:"space-between", alignItems:"baseline", gap:8, marginBottom:2}}>
                      <b style={{fontSize:13, color:"#0F172A"}}>{it.title}</b>
                      <small style={{fontSize:10, color:"#94A3B8", whiteSpace:"nowrap"}}>{formatRelative(it.created_at)}</small>
                    </div>
                    {it.body && (
                      <div style={{fontSize:12, color:"#475569", lineHeight:1.4}}>{it.body}</div>
                    )}
                    <div style={{display:"flex", gap:6, marginTop:6, alignItems:"center"}}>
                      <span style={{
                        background: tone.chip, color: tone.chipFg,
                        padding:"2px 8px", borderRadius:999,
                        fontSize:9, fontWeight:800, textTransform:"uppercase", letterSpacing:0.3,
                      }}>{it.event?.replaceAll("_"," ")}</span>
                      {it.link && (
                        <button
                          data-testid={`inbox-item-open-${it.id}`}
                          onClick={e => {
                            e.stopPropagation();
                            if (isUnread) markRead(it.id);
                            if (it.link.startsWith("http")) {
                              window.open(it.link, "_blank", "noopener");
                            } else {
                              // Internal /workspace?tab=...&job=... — client-side nav
                              navigate(it.link);
                              setOpen(false);
                            }
                          }}
                          style={{
                            background:"transparent", border:"none",
                            fontSize:11, color:"#2563EB", textDecoration:"none",
                            cursor:"pointer", padding:0,
                          }}
                        >Open →</button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </aside>
      )}
    </>
  );
}
